import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import GroupsIcon from "@mui/icons-material/Groups";
import ArticleIcon from "@mui/icons-material/Article";
import ShieldIcon from "@mui/icons-material/Shield";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import PhoneIcon from "@mui/icons-material/Phone";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockIcon from "@mui/icons-material/Lock";
import ClearIcon from "@mui/icons-material/Clear";
import BadgeIcon from "@mui/icons-material/Badge";
import { useLocation, useNavigate } from "react-router-dom";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import safeToast from "utils/safeToast";
import { API_ENDPOINTS } from "config/api";
import { decryptText, encryptText } from "config/encryption";
import axiosInstance from "API/axiosInstance";
import ksrAlumniPortalLogo from "assets/images/ksr-alumni-portal-logo.png";
import studentRequestsImage from "assets/images/student-requests.jpg";
import uploadCurrentStudentsImage from "assets/images/upload-current-students.jpg";
import allAlumniImage from "assets/images/all-alumni.jpg";
import alumniRightsImage from "assets/images/alumni-rights.jpg";

const NAVY = "#1e2a78";
const NAVY_DEEP = "#151d5c";
const ACCENT = "#3f51b5";
const FEATURE_BG = "rgba(255,255,255,0.12)";
const SOFT_BORDER = "rgba(30, 42, 120, 0.14)";
const SOFT_SHADOW = "0 10px 30px rgba(30, 42, 120, 0.10)";

function ProgressIndicator({ total, active }) {
  return (
    <Box display="flex" alignItems="center" gap={1} mb={2}>
      {Array.from({ length: total }).map((_, i) => {
        const n = i + 1;
        const isActive = n === active;
        return (
          <Box
            key={i}
            sx={{
              width: isActive ? 28 : 8,
              height: 8,
              borderRadius: 999,
              bgcolor: isActive ? ACCENT : "rgba(0,0,0,0.12)",
              transition: "all 0.25s ease",
            }}
          />
        );
      })}
    </Box>
  );
}

ProgressIndicator.propTypes = {
  total: PropTypes.number.isRequired,
  active: PropTypes.number.isRequired,
};

function BrandingPanel({ compact }) {
  const isDesktopPanel = !compact;
  const panelPrimaryText = isDesktopPanel ? NAVY_DEEP : "#fff";
  const panelSecondaryText = isDesktopPanel ? "rgba(21,29,92,0.75)" : "rgba(255,255,255,0.85)";
  const panelSubtleText = isDesktopPanel ? "rgba(21,29,92,0.85)" : "#ffb74d";
  const panelFeatureBg = isDesktopPanel ? "rgba(63, 81, 181, 0.08)" : FEATURE_BG;
  const panelEdgeFade = isDesktopPanel ? "#ffffff" : NAVY;

  const features = [
    { icon: <CardMembershipIcon sx={{ fontSize: 22 }} />, text: "Access your certificates" },
    { icon: <GroupsIcon sx={{ fontSize: 22 }} />, text: "Connect with alumni" },
    { icon: <ArticleIcon sx={{ fontSize: 22 }} />, text: "View academic records" },
  ];
  const showcaseImages = [
    { src: studentRequestsImage, alt: "Student requests" },
    { src: uploadCurrentStudentsImage, alt: "Upload current students" },
    { src: allAlumniImage, alt: "All alumni" },
    { src: alumniRightsImage, alt: "Alumni rights" },
  ];
  const [activeShowcaseIndex, setActiveShowcaseIndex] = useState(0);

  useEffect(() => {
    if (compact) return undefined;

    const intervalId = window.setInterval(() => {
      setActiveShowcaseIndex((prev) => (prev + 1) % showcaseImages.length);
    }, 2000);

    return () => window.clearInterval(intervalId);
  }, [compact, showcaseImages.length]);

  return (
    <Box
      sx={{
        background: isDesktopPanel
          ? "#ffffff"
          : `linear-gradient(165deg, ${NAVY_DEEP} 0%, ${NAVY} 45%, #283593 100%)`,
        color: panelPrimaryText,
        px: { xs: 3, md: 4 },
        py: { xs: 4, md: 6 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        minHeight: compact ? "auto" : "100%",
        justifyContent: "flex-start",
        gap: compact ? 0 : 3,
        borderRadius: compact ? "0 0 28px 28px" : 0,
      }}
    >
      <Box width="100%">
        <Box
          sx={{
            bgcolor: "rgba(255,255,255,0.97)",
            borderRadius: 2,
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1.25, sm: 1.5 },
            maxWidth: compact ? 300 : 560,
            mx: "auto",
            mb: compact ? 2 : 2.5,
            boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
          }}
        >
          <Box
            component="img"
            src={ksrAlumniPortalLogo}
            alt="K.S. Rangasamy College of Technology"
            sx={{
              width: "100%",
              height: "auto",
              display: "block",
              maxHeight: compact ? 72 : 140,
              objectFit: "contain",
            }}
          />
        </Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.2rem", md: "1.6rem" },
            letterSpacing: "-0.02em",
            mb: 0.5,
            color: panelPrimaryText,
          }}
        >
          
        </Typography>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.1,
            py: 0.45,
            borderRadius: 999,
            bgcolor: "rgba(63, 81, 181, 0.08)",
            mb: 1,
          }}
        >
          <BadgeIcon sx={{ color: ACCENT, fontSize: 16 }} />
          <Typography
            sx={{
              color: panelPrimaryText,
              fontSize: { xs: "0.68rem", md: "0.78rem" },
              fontWeight: 600,
              letterSpacing: 0.25,
              lineHeight: 1.1,
            }}
          >
            Welcome to KSR Alumni Portal
          </Typography>
        </Box>
        <Typography
          sx={{
            color: panelSubtleText,
            fontSize: { xs: "0.65rem", sm: "0.72rem", md: "0.85rem" },
            fontWeight: 600,
            letterSpacing: 0.6,
            mb: compact ? 1.25 : 1.5,
          }}
        >
          Powered By IMPRES ERP
        </Typography>
        <Typography
          sx={{
            color: panelSecondaryText,
            fontSize: { xs: "0.8rem", md: "1.02rem" },
            maxWidth: compact ? 300 : 460,
            mx: "auto",
            lineHeight: 1.5,
            mb: compact ? 3 : 0,
          }}
        >
          {compact
            ? "Verify your identity to continue"
            : "Your gateway to the alumni network, resources and records."}
        </Typography>
      </Box>

      {!compact && (
        <Box width="100%" maxWidth={560} sx={{ mt: { md: 2.5 }, mb: { md: 2 } }}>
          <Box
            sx={{
              position: "relative",
              borderRadius: 3,
              mb: { xs: 2.5, md: 3 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.25,
            }}
          >
            <Box
              key={showcaseImages[activeShowcaseIndex].alt}
              sx={{
                width: { xs: "100%", md: 300 },
                maxWidth: { xs: 300, md: 300 },
                height: { xs: 170, md: 190 },
                borderRadius: 2.5,
                overflow: "hidden",
                boxShadow: "0 8px 28px rgba(0,0,0,0.2)",
                border: "1px solid rgba(0,0,0,0.08)",
                mx: "auto",
              }}
            >
              <Box
                component="img"
                src={showcaseImages[activeShowcaseIndex].src}
                alt={showcaseImages[activeShowcaseIndex].alt}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Box>

            <Box display="flex" alignItems="center" justifyContent="center" gap={0.75}>
              {showcaseImages.map((item, index) => (
                <Box
                  key={item.alt}
                  sx={{
                    width: index === activeShowcaseIndex ? 18 : 7,
                    height: 7,
                    borderRadius: 999,
                    bgcolor: index === activeShowcaseIndex ? ACCENT : "rgba(0,0,0,0.16)",
                    transition: "all 0.25s ease",
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              borderRadius: 2,
              px: 0.25,
              mt: { xs: 13.25, md: 13.5 },
              mb: { md: 13.5 },
              "&::before, &::after": {
                content: '""',
                position: "absolute",
                top: 0,
                width: 32,
                height: "100%",
                zIndex: 2,
                pointerEvents: "none",
              },
              "&::before": {
                left: 0,
                background: `linear-gradient(to right, ${panelEdgeFade} 0%, rgba(255,255,255,0) 100%)`,
              },
              "&::after": {
                right: 0,
                background: `linear-gradient(to left, ${panelEdgeFade} 0%, rgba(255,255,255,0) 100%)`,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                width: "max-content",
                gap: 1.25,
                animation: "featureTicker 16s linear infinite",
                "@keyframes featureTicker": {
                  from: { transform: "translateX(0)" },
                  to: { transform: "translateX(calc(-50% - 0.625rem))" },
                },
                "&:hover": {
                  animationPlayState: "paused",
                },
              }}
            >
              {[...features, ...features].map((f, index) => (
                <Box
                  key={`${f.text}-${index}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    py: 1.2,
                    px: 2,
                    borderRadius: 2,
                    bgcolor: panelFeatureBg,
                    minWidth: 260,
                    textAlign: "left",
                  }}
                >
                  <Box sx={{ display: "flex", color: panelPrimaryText }}>{f.icon}</Box>
                  <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, whiteSpace: "nowrap" }}>
                    {f.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

BrandingPanel.propTypes = {
  compact: PropTypes.bool,
};

BrandingPanel.defaultProps = {
  compact: false,
};

function SelectionCard({ selected, onClick, icon, title, subtitle, disabled }) {
  return (
    <Button
      fullWidth
      onClick={onClick}
      disabled={disabled}
      sx={{
        flex: 1,
        minHeight: { xs: 140, md: 178 },
        p: { xs: 2, md: 2.5 },
        borderRadius: 2,
        textTransform: "none",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        border: selected ? `2px solid ${ACCENT}` : `1px solid ${SOFT_BORDER}`,
        bgcolor: selected ? "rgba(63, 81, 181, 0.07)" : "#fff",
        color: "text.primary",
        boxShadow: selected ? "0 10px 28px rgba(63, 81, 181, 0.18)" : "0 4px 16px rgba(15, 23, 42, 0.06)",
        transition: "all 0.25s ease",
        "&:hover": {
          bgcolor: selected ? "rgba(63, 81, 181, 0.11)" : "rgba(63, 81, 181, 0.03)",
          borderColor: selected ? ACCENT : "rgba(63, 81, 181, 0.35)",
          boxShadow: selected
            ? "0 12px 30px rgba(63, 81, 181, 0.22)"
            : "0 10px 24px rgba(63, 81, 181, 0.14)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box mb={1}>{icon}</Box>
      <Typography fontWeight={700} color={NAVY} sx={{ fontSize: { xs: "1rem", md: "1.35rem" } }}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: "0.8rem", md: "1rem" } }}>
        {subtitle}
      </Typography>
    </Button>
  );
}

SelectionCard.propTypes = {
  selected: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

SelectionCard.defaultProps = {
  selected: false,
  disabled: false,
};

function CheckingAuth() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [rollNo, setRollNo] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState(null);
  const [userType, setUserType] = useState(null);

  /** @type {'role' | 'method' | 'credential'} */
  const [phase, setPhase] = useState("role");

  const location = useLocation();
  const navigate = useNavigate();

  const { progressTotal, progressActive } = useMemo(() => {
    if (phase === "role") {
      return { progressTotal: 3, progressActive: 1 };
    }
    if (userType === "staff") {
      return { progressTotal: 2, progressActive: phase === "credential" ? 2 : 1 };
    }
    return {
      progressTotal: 3,
      progressActive: phase === "method" ? 2 : phase === "credential" ? 3 : 1,
    };
  }, [phase, userType]);

  const decryptResponse = useCallback((encryptedText) => {
    try {
      return decryptText(encryptedText);
    } catch (err) {
      console.error("Decryption error:", err);
      return null;
    }
  }, []);

  useEffect(() => {
    const msg = location.state?.message;
    if (msg) setError(msg);

    const sessionData = localStorage.getItem("userSession");
    const sessionLoggedUserDetail = localStorage.getItem("UserLogDetails");

    if (sessionData && sessionLoggedUserDetail) {
      try {
        const decryptedUserDetails = decryptText(sessionData);
        const parsedUserDetails = JSON.parse(decryptedUserDetails);
        const usn = parsedUserDetails?.usn || "";

        const decryptedLoggedUserDetails = decryptText(sessionLoggedUserDetail);
        const parsedLoggedUserDetails = JSON.parse(decryptedLoggedUserDetails);
        const lus = parsedLoggedUserDetails?.UserName || "";

        if (usn === lus) {
          safeToast.warn("Already found Login. Redirecting to Myprofile...");
          setTimeout(() => {
            navigate("/MyDetails");
          }, 1000);
        }
      } catch {
        /* ignore */
      }
    }
  }, [location.state, navigate]);

  const handleRoleSelect = (type) => {
    setError("");
    setUserType(type);
    if (type === "staff") {
      setLoginMode("roll");
      setPhase("credential");
    } else {
      setLoginMode(null);
      setPhase("method");
    }
  };

  const handleMethodContinue = () => {
    setError("");
    if (loginMode == null) {
      setError("Please select a login method.");
      return;
    }
    setPhase("credential");
  };

  const handleBack = () => {
    setError("");
    setMessage("");
    if (phase === "credential") {
      if (userType === "staff") {
        setPhase("role");
        setRollNo("");
      } else {
        setPhase("method");
        setRollNo("");
        setMobileNo("");
      }
    } else if (phase === "method") {
      setPhase("role");
      setLoginMode(null);
    }
  };

  const handleCheck = async () => {
    setError("");
    setMessage("");
    setLoading(true);

    const input = loginMode === "roll" ? rollNo.trim().toUpperCase() : mobileNo.trim();
    if (!input) {
      setError(
        `Please enter a valid ${loginMode === "roll" ? (userType === "student" ? "Alumni Code" : "Login Code") : "Mobile Number"}.`
      );
      setLoading(false);
      return;
    }

    if (userType === "staff" && loginMode === "mobile") {
      setError("This feature is under development.");
      setLoading(false);
      return;
    }

    if (loginMode === "mobile" && input.length !== 10) {
      setError("Please enter a valid Mobile Number.");
      setLoading(false);
      return;
    }

    try {
      let decrypted;

      if (userType === "staff" && loginMode === "roll") {
        const encryptedLoginCode = encryptText(input);
        const { data } = await axiosInstance.post(API_ENDPOINTS.AUTH.CHECK_STAFF_BY_LOGIN_CODE, {
          loginCode: encryptedLoginCode,
        });
        decrypted = decryptResponse(data);

        if (decrypted) {
          localStorage.removeItem("adminsessiondetails");
          localStorage.setItem("adminsessiondetails", data);
          setMessage("Admin found. Redirecting...");
          setTimeout(() => {
            navigate("/AdminLogin");
          }, 2000);
        } else {
          setError("Unable to decrypt admin response.");
        }
      } else {
        const { data: encrypted } = await axiosInstance.post(
          API_ENDPOINTS.AUTH.CHECK_USER,
          loginMode === "roll" ? { rollNum: input } : { mobile: input }
        );
        decrypted = decryptResponse(encrypted);

        if (decrypted) {
          localStorage.removeItem("userSession");
          localStorage.removeItem("UserLogDetails");
          localStorage.setItem("userSession", encrypted);
          setMessage("User found. Redirecting...");
          setTimeout(() => {
            navigate("/authentication/sign-in");
          }, 2000);
        } else {
          setError("Unable to decrypt response.");
        }
      }
    } catch (err) {
      console.error("CheckUser error:", err);
      setError("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setRollNo("");
    setMobileNo("");
    setMessage("");
    setError("");
  };

  const roleSubtitle = "Hello there! I am a…";
  const signedInLabel = userType === "staff" ? "Admin" : "Alumni";

  const rightPanelContent = (
    <Box
      sx={{
        px: { xs: 2.5, sm: 4, md: 6 },
        py: { xs: 3, sm: 4, md: 5 },
        maxWidth: { xs: 520, md: 820 },
        mx: "auto",
        width: "100%",
        borderRadius: { md: 3 },
        border: { md: `1px solid ${SOFT_BORDER}` },
        boxShadow: { md: SOFT_SHADOW },
      }}
    >
      <ProgressIndicator total={progressTotal} active={progressActive} />

      {phase !== "role" && (
        <Button
          fullWidth
          variant="outlined"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          sx={{
            mb: 2,
            py: 1.25,
            borderRadius: 2,
            textTransform: "none",
            color: "text.primary",
            borderColor: "grey.300",
            fontWeight: 600,
          }}
        >
          Back
        </Button>
      )}

      {phase !== "role" && (
        <Box
          sx={{
            py: 1.25,
            px: 2,
            mb: 2,
            borderRadius: 2,
            bgcolor: "rgba(245, 235, 220, 0.55)",
            border: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {phase === "credential" ? "Signed in as" : "Logged in as"}{" "}
            <Box component="span" fontWeight={700} color="text.primary">
              {signedInLabel}
            </Box>
          </Typography>
        </Box>
      )}

      {phase === "role" && (
        <>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              bgcolor: "rgba(63, 81, 181, 0.08)",
              mb: 2,
            }}
          >
            <GroupsIcon sx={{ color: ACCENT, fontSize: 18 }} />
            <Typography
              variant="overline"
              sx={{
                color: "text.secondary",
                letterSpacing: 1.5,
                fontWeight: 700,
                display: "block",
                fontSize: { xs: "0.72rem", md: "0.92rem" },
              }}
            >
              {roleSubtitle}
            </Typography>
          </Box>
          <Box display="flex" gap={2} flexDirection={{ xs: "column", sm: "row" }}>
            <SelectionCard
              selected={userType === "student"}
              onClick={() => handleRoleSelect("student")}
              icon={
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    bgcolor: "#fce4ec",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CardMembershipIcon sx={{ color: "#d81b60", fontSize: 26 }} />
                </Box>
              }
              title="Alumni"
              subtitle="Graduated student"
            />
            <SelectionCard
              selected={userType === "staff"}
              onClick={() => handleRoleSelect("staff")}
              icon={
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    bgcolor: "rgba(63, 81, 181, 0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ShieldIcon sx={{ color: ACCENT, fontSize: 26 }} />
                </Box>
              }
              title="Admin"
              subtitle="Staff / portal admin"
            />
          </Box>
        </>
      )}

      {phase === "method" && userType === "student" && (
        <>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              bgcolor: "rgba(63, 81, 181, 0.08)",
              mb: 2,
            }}
          >
            <LockIcon sx={{ color: ACCENT, fontSize: 18 }} />
            <Typography
              variant="overline"
              sx={{
                color: "text.secondary",
                letterSpacing: 1.5,
                fontWeight: 700,
                display: "block",
                fontSize: { xs: "0.72rem", md: "0.92rem" },
              }}
            >
              Login method
            </Typography>
          </Box>
          <Box display="flex" gap={2} flexDirection={{ xs: "column", sm: "row" }}>
            <SelectionCard
              selected={loginMode === "roll"}
              onClick={() => {
                setLoginMode("roll");
                setError("");
              }}
              icon={
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    bgcolor: "#fff3e0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <VpnKeyIcon sx={{ color: "#f57c00", fontSize: 26 }} />
                </Box>
              }
              title="Alumni Code"
              subtitle="e.g. ERPS001"
            />
            <SelectionCard
              selected={loginMode === "mobile"}
              onClick={() => {
                setLoginMode("mobile");
                setError("");
              }}
              icon={
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    bgcolor: "#e8f5e9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PhoneIcon sx={{ color: "#43a047", fontSize: 26 }} />
                </Box>
              }
              title="Mobile Number"
              subtitle="OTP verification"
            />
          </Box>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleMethodContinue}
            endIcon={<ArrowForwardIcon />}
            disabled={loginMode == null}
            sx={{
              mt: 3,
              py: { xs: 1.35, md: 1.8 },
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "1rem", md: "1.25rem" },
              borderColor: "grey.300",
              color: loginMode == null ? "text.disabled" : "text.primary",
            }}
          >
            Continue
          </Button>
        </>
      )}

      {phase === "credential" && (
        <>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              bgcolor: "rgba(63, 81, 181, 0.08)",
              mb: 1,
            }}
          >
            {loginMode === "roll" ? (
              <VpnKeyIcon sx={{ color: ACCENT, fontSize: 18 }} />
            ) : (
              <PhoneIcon sx={{ color: ACCENT, fontSize: 18 }} />
            )}
            <Typography
              variant="overline"
              sx={{
                color: "text.secondary",
                letterSpacing: 1.2,
                fontWeight: 700,
                display: "block",
                fontSize: { xs: "0.72rem", md: "0.92rem" },
              }}
            >
              {loginMode === "roll"
                ? userType === "student"
                  ? "Alumni code"
                  : "Login code"
                : "Mobile number"}
            </Typography>
          </Box>

          {loginMode === "roll" ? (
            <TextField
              fullWidth
              placeholder={userType === "student" ? "e.g. ERPS001" : "e.g. ADMIN001"}
              value={rollNo}
              onChange={(e) => setRollNo(e.target.value.toUpperCase())}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        pr: 1,
                        mr: 1,
                        borderRight: "1px solid",
                        borderColor: "grey.300",
                      }}
                    >
                      <VpnKeyIcon sx={{ color: "grey.500", fontSize: 22 }} />
                    </Box>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "grey.50",
                  fontSize: { xs: "1rem", md: "1.2rem" },
                },
              }}
            />
          ) : (
            <TextField
              fullWidth
              label="Mobile Number"
              placeholder="e.g. 9876543210"
              value={mobileNo}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                setMobileNo(value);
              }}
              type="tel"
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon sx={{ color: "grey.500", fontSize: 22 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "grey.50",
                  fontSize: { xs: "1rem", md: "1.2rem" },
                },
              }}
            />
          )}

          <Button
            fullWidth
            variant="outlined"
            onClick={handleCheck}
            disabled={loading || (userType === "staff" && loginMode === "mobile")}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <LockIcon />}
            sx={{
              mb: 1.5,
              py: { xs: 1.35, md: 1.8 },
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "1rem", md: "1.2rem" },
              borderColor: "grey.300",
              color: "text.primary",
            }}
          >
            {loading ? "Verifying…" : "Verify & Continue"}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleReset}
            disabled={loading}
            startIcon={<ClearIcon />}
            sx={{
              py: { xs: 1.35, md: 1.8 },
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "1rem", md: "1.2rem" },
              borderColor: "grey.300",
              color: "text.primary",
            }}
          >
            Clear
          </Button>

          {userType === "staff" && loginMode === "mobile" && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
              This feature is under development.
            </Alert>
          )}
        </>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mt: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" onClose={() => setMessage("")} sx={{ mt: 2, borderRadius: 2 }}>
          {message}
        </Alert>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: isDesktop ? "#f5f6fa" : "#fff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {isDesktop ? (
        <Grid container sx={{ flex: 1, minHeight: "100vh" }}>
          <Grid
            item
            md={5}
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <BrandingPanel compact={false} />
          </Grid>
          <Grid
            item
            md={7}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#fff",
            }}
          >
            {rightPanelContent}
          </Grid>
        </Grid>
      ) : (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <BrandingPanel compact />
          <Box sx={{ flex: 1, bgcolor: "#fff", borderRadius: "24px 24px 0 0", mt: -2, pt: 1 }}>
            {rightPanelContent}
          </Box>
        </Box>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </Box>
  );
}

export default CheckingAuth;
